import { useContext, useMemo } from "react";

import { Permission } from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import DepartmentIdentityContext from "src/contexts/data/DepartmentIdentityContext";
import GroupIdentityContext from "src/contexts/data/GroupIdentityContext";
import InstitutionFormIdentityContext from "src/contexts/data/InstitutionFormIdentityContext";
import InstitutionIdentityContext from "src/contexts/data/InstitutionIdentityContext";
import ItemComponentIdentityContext from "src/contexts/data/ItemComponentIdentityContext";
import PatientIdentityContext from "src/contexts/data/PatientIdentityContext";
import PatientItemIdentityContext from "src/contexts/data/PatientItemIdentityContext";
import PersonnelIdentityContext from "src/contexts/data/PersonnelIdentityContext";
import RoleIdentityContext from "src/contexts/data/RoleIdentityContext";
import TrialFormIdentityContext from "src/contexts/data/TrialFormIdentityContext";
import TrialGroupIdentityContext from "src/contexts/data/TrialGroupIdentityContext";
import TrialIdentityContext from "src/contexts/data/TrialIdentityContext";
import TrialInstitutionIdentityContext from "src/contexts/data/TrialInstitutionIdentityContext";
import TrialInstitutionItemIdentityContext from "src/contexts/data/TrialInstitutionItemIdentityContext";
import TrialInstitutionSupportingSiteIdentityContext from "src/contexts/data/TrialInstitutionSupportingSiteIdentityContext";
import TrialItemIdentityContext from "src/contexts/data/TrialItemIdentityContext";
import type { ILayoutBreadcrumb } from "src/contexts/layouts/LayoutContext";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHasPermission from "src/hooks/authentication/useHasPermission";

import SetIdentityBreadcrumb from "./SetIdentityBreadcrumb";
import SetPageBreadcrumbs from "./SetPageBreadcrumbs";

const identityBreadcrumb = (
    code: string,
    label: string | undefined,
    href: string | undefined,
    icon: ILayoutBreadcrumb["icon"],
    order: number,
    loading: boolean
): ILayoutBreadcrumb => ({ code, label, href, icon, order, loading });

/**
 * Registers the trial identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const TrialIdentityBreadcrumb = () => {
    const state = useContext(TrialIdentityContext);
    const identity = state.data;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial",
                identity ? `${identity.displayNumber} ${identity.displayTitle}`.trim() : undefined,
                identity?.id ? `/trials/${identity.id}/details` : undefined,
                AppSettings.icons.models.trial,
                20,
                state.loading
            )
        ],
        [identity, state.loading]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the trial institution identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const TrialInstitutionIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const state = useContext(TrialInstitutionIdentityContext);
    const identity = state.data;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-institution",
                identity?.institutionName,
                identity?.id && trialState.data?.id
                    ? `/trials/${trialState.data.id}/institutions/${identity.id}/details`
                    : undefined,
                AppSettings.icons.models.trialInstitution,
                40,
                state.loading
            )
        ],
        [identity, state.loading, trialState.data?.id]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-institution-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/** Places the linked Group name between the trial's Groups section and its child page. */
export const TrialGroupIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const state = useContext(TrialGroupIdentityContext);
    const identity = state.data;
    const trialId = trialState.data?.id;
    const matchedIdentity = identity?.trialId === trialId ? identity : undefined;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-group",
                matchedIdentity?.groupName,
                matchedIdentity?.id && trialId
                    ? `/trials/${trialId}/groups/${matchedIdentity.id}/details`
                    : undefined,
                AppSettings.icons.models.trialGroup,
                40,
                state.loading
            )
        ],
        [matchedIdentity, state.loading, trialId]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-group-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the supporting site identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Order 60 places it after the trial institution crumb (40) and the
 * trial institution section crumb (50). Layout updates occur through the registration effect.
 */
export const TrialInstitutionSupportingSiteIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const trialInstitutionState = useContext(TrialInstitutionIdentityContext);
    const state = useContext(TrialInstitutionSupportingSiteIdentityContext);
    const identity = state.data;
    const trialId = trialState.data?.id;
    const trialInstitutionId = trialInstitutionState.data?.id;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-institution-supporting-site",
                identity?.institutionName,
                identity?.id && trialId && trialInstitutionId
                    ? `/trials/${trialId}/institutions/${trialInstitutionId}` +
                          `/supporting-sites/${identity.id}/details`
                    : undefined,
                AppSettings.icons.models.trialInstitutionSupportingSite,
                60,
                state.loading
            )
        ],
        [identity, state.loading, trialId, trialInstitutionId]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-institution-supporting-site-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the patient identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const PatientIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const trialInstitutionState = useContext(TrialInstitutionIdentityContext);
    const state = useContext(PatientIdentityContext);
    const identity = state.data;
    const href =
        identity?.id && trialState.data?.id
            ? trialInstitutionState.data?.id
                ? `/trials/${trialState.data.id}/institutions/${trialInstitutionState.data.id}/participants/${identity.id}/details`
                : `/trials/${trialState.data.id}/participants/${identity.id}/details`
            : undefined;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "patient",
                identity?.registrationNumber,
                href,
                AppSettings.icons.models.participant,
                trialInstitutionState.data ? 60 : 40,
                state.loading
            )
        ],
        [href, identity?.registrationNumber, state.loading, trialInstitutionState.data]
    );

    return (
        <SetPageBreadcrumbs
            owner="patient-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the institution identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const InstitutionIdentityBreadcrumb = () => {
    const state = useContext(InstitutionIdentityContext);
    const identity = state.data;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "institution",
                identity?.name,
                identity?.id ? `/institutions/${identity.id}/details` : undefined,
                AppSettings.icons.models.institution,
                20,
                state.loading
            )
        ],
        [identity, state.loading]
    );

    return (
        <SetPageBreadcrumbs
            owner="institution-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the department identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const DepartmentIdentityBreadcrumb = () => {
    const state = useContext(DepartmentIdentityContext);
    const identity = state.data;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "department",
                identity?.name,
                identity?.id && identity.institutionId
                    ? `/institutions/${identity.institutionId}/departments/${identity.id}/details`
                    : undefined,
                AppSettings.icons.models.department,
                40,
                state.loading
            )
        ],
        [identity, state.loading]
    );

    return (
        <SetPageBreadcrumbs
            owner="department-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the group identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const GroupIdentityBreadcrumb = () => {
    const state = useContext(GroupIdentityContext);
    const identity = state.data;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "group",
                identity?.name,
                identity?.id ? `/groups/${identity.id}/details` : undefined,
                AppSettings.icons.models.group,
                20,
                state.loading
            )
        ],
        [identity, state.loading]
    );

    return (
        <SetPageBreadcrumbs
            owner="group-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the personnel identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const PersonnelIdentityBreadcrumb = () => {
    const state = useContext(PersonnelIdentityContext);
    const identity = state.data;
    const label = identity ? `${identity.firstName} ${identity.lastName}`.trim() : undefined;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "personnel",
                label,
                identity?.id ? `/personnel/${identity.id}/details` : undefined,
                AppSettings.icons.models.personnel,
                20,
                state.loading
            )
        ],
        [identity?.id, label, state.loading]
    );

    return (
        <SetPageBreadcrumbs
            owner="personnel-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the trial form identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const TrialFormIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const state = useContext(TrialFormIdentityContext);
    const identity = state.data;
    const permissions = useCurrentUserPermissions();
    // Prefer the view route when allowed; otherwise expose entry only with its matching permission.
    const canView = useHasPermission(
        permissions,
        Permission.TrialFormView,
        identity?.trialId,
        identity?.institutionId
    );
    const canEnterData = useHasPermission(
        permissions,
        Permission.TrialFormDataEntry,
        identity?.trialId,
        identity?.institutionId
    );
    const label = identity
        ? [identity.formTemplateName, identity.patientRegistrationNumber]
              .filter(Boolean)
              .join(" — ")
        : undefined;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-form",
                label,
                identity?.id && trialState.data?.id && (canView || canEnterData)
                    ? `/trials/${trialState.data.id}/forms/${identity.id}${canView ? "" : "/entry"}`
                    : undefined,
                AppSettings.icons.models.trialForm,
                40,
                state.loading
            )
        ],
        [canEnterData, canView, identity?.id, label, state.loading, trialState.data?.id]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-form-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the trial item identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const TrialItemIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const state = useContext(TrialItemIdentityContext);
    const identity = state.data;
    const permissions = useCurrentUserPermissions();
    // Prefer the view route when allowed; otherwise expose entry only with its matching permission.
    const canView = useHasPermission(permissions, Permission.TrialItemView, identity?.trialId);
    const canUpdate = useHasPermission(permissions, Permission.TrialItemUpdate, identity?.trialId);
    const canEnterData = useHasPermission(
        permissions,
        Permission.TrialItemDataEntry,
        identity?.trialId
    );
    const label = identity
        ? [identity.trialItemTemplateName, identity.instanceNumber]
              .filter(value => value !== null && value !== undefined && value !== "")
              .join(" — ")
        : undefined;
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-item",
                label,
                identity?.id && trialState.data?.id && (canView || canUpdate || canEnterData)
                    ? `/trials/${trialState.data.id}/items/trial-items/${identity.id}${canView ? "" : "/entry"}`
                    : undefined,
                AppSettings.icons.models.trialItem,
                50,
                state.loading
            )
        ],
        [canEnterData, canUpdate, canView, identity?.id, label, state.loading, trialState.data?.id]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-item-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the trial institution item identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const TrialInstitutionItemIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const trialInstitutionState = useContext(TrialInstitutionIdentityContext);
    const state = useContext(TrialInstitutionItemIdentityContext);
    const identity = state.data;
    const permissions = useCurrentUserPermissions();
    const canView = useHasPermission(
        permissions,
        Permission.TrialInstitutionItemInstanceView,
        identity?.trialId,
        identity?.institutionId
    );
    const canEnterData = useHasPermission(
        permissions,
        Permission.TrialInstitutionItemDataEntry,
        identity?.trialId,
        identity?.institutionId
    );
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "trial-institution-item",
                identity?.trialInstitutionItemTemplateName,
                identity?.id &&
                    (canView || canEnterData) &&
                    trialState.data?.id &&
                    trialInstitutionState.data?.id
                    ? `/trials/${trialState.data.id}/institutions/${trialInstitutionState.data.id}/items/${identity.id}${canView ? "" : "/entry"}`
                    : undefined,
                AppSettings.icons.models.trialInstitutionItem,
                60,
                state.loading
            )
        ],
        [
            canView,
            canEnterData,
            identity?.id,
            identity?.trialInstitutionItemTemplateName,
            state.loading,
            trialInstitutionState.data?.id,
            trialState.data?.id
        ]
    );

    return (
        <SetPageBreadcrumbs
            owner="trial-institution-item-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the patient item identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const PatientItemIdentityBreadcrumb = () => {
    const trialState = useContext(TrialIdentityContext);
    const patientState = useContext(PatientIdentityContext);
    const state = useContext(PatientItemIdentityContext);
    const identity = state.data;
    const permissions = useCurrentUserPermissions();
    const canView = useHasPermission(
        permissions,
        Permission.PatientItemView,
        identity?.trialId,
        identity?.institutionId
    );
    const canEnterData = useHasPermission(
        permissions,
        Permission.PatientItemDataEntry,
        identity?.trialId,
        identity?.institutionId
    );
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "patient-item",
                identity?.patientItemTemplateName,
                identity?.id &&
                    (canView || canEnterData) &&
                    trialState.data?.id &&
                    patientState.data?.id
                    ? `/trials/${trialState.data.id}/participants/${patientState.data.id}/items/${identity.id}${canView ? "" : "/entry"}`
                    : undefined,
                AppSettings.icons.models.patientItem,
                70,
                state.loading
            )
        ],
        [
            canView,
            canEnterData,
            identity?.id,
            identity?.patientItemTemplateName,
            patientState.data?.id,
            state.loading,
            trialState.data?.id
        ]
    );

    return (
        <SetPageBreadcrumbs
            owner="patient-item-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the institution form identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const InstitutionFormIdentityBreadcrumb = () => {
    const institutionState = useContext(InstitutionIdentityContext);
    const state = useContext(InstitutionFormIdentityContext);
    const identity = state.data;
    const permissions = useCurrentUserPermissions();
    // Prefer the view route when allowed; otherwise expose entry only with its matching permission.
    const canView = useHasPermission(
        permissions,
        Permission.InstitutionFormView,
        undefined,
        identity?.institutionId
    );
    const canEnterData = useHasPermission(
        permissions,
        Permission.InstitutionFormDataEntryView,
        undefined,
        identity?.institutionId
    );
    const breadcrumbs = useMemo(
        () => [
            identityBreadcrumb(
                "institution-form",
                identity?.formTemplateName,
                identity?.id && institutionState.data?.id && (canView || canEnterData)
                    ? `/institutions/${institutionState.data.id}/forms/${identity.id}${canView ? "" : "/entry"}`
                    : undefined,
                AppSettings.icons.models.institutionForm,
                40,
                state.loading
            )
        ],
        [
            canEnterData,
            canView,
            identity?.formTemplateName,
            identity?.id,
            institutionState.data?.id,
            state.loading
        ]
    );

    return (
        <SetPageBreadcrumbs
            owner="institution-form-identity"
            breadcrumbs={breadcrumbs}
        />
    );
};

/**
 * Registers the role identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const RoleIdentityBreadcrumb = () => {
    const state = useContext(RoleIdentityContext);
    const identity = state.data;
    return (
        <SetIdentityBreadcrumb
            owner="role-identity"
            code="role"
            label={identity?.value}
            href={identity?.id ? `/settings/general/roles/${identity.id}` : undefined}
            icon={AppSettings.icons.models.role}
            order={40}
            loading={state.loading}
        />
    );
};

/**
 * Registers the item component identity breadcrumb from current context.
 *
 * Postconditions: The registration reflects current identity/loading state; missing required IDs
 * leave the link undefined. Layout updates occur through the breadcrumb registration effect.
 */
export const ItemComponentIdentityBreadcrumb = () => {
    const state = useContext(ItemComponentIdentityContext);
    const identity = state.data;
    return (
        <SetIdentityBreadcrumb
            owner="item-component-identity"
            code="item-component"
            label={identity?.name}
            href={
                identity?.id
                    ? createItemComponentBreadcrumbHref(identity.id, identity.itemComponentTypeId)
                    : undefined
            }
            icon={AppSettings.icons.models.itemComponent}
            order={40}
            loading={state.loading}
        />
    );
};

/**
 * Routes component types 3, 4 and 5 to QA form components; all other types use item components.
 */
const createItemComponentBreadcrumbHref = (id: number, itemComponentTypeId?: number) =>
    itemComponentTypeId === 3 || itemComponentTypeId === 4 || itemComponentTypeId === 5
        ? `/settings/qa/formcomponents/${id}`
        : `/settings/item/itemcomponents/${id}`;
